import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Volume2, VolumeX, X } from "lucide-react";
import { TalkingPortrait } from "../coach/TalkingPortrait";
import { match } from "../../pages/Guide/GuideLitePage";
import { DOORS, door } from "../../pages/Ecosystem/doors";
import { goTo } from "./ReclaimNav";

const API = process.env.REACT_APP_BACKEND_URL;
const SERIF = '"Cormorant Garamond", serif';
const SANS = '"Manrope", sans-serif';
const GOLD = "#D7BB7A";
const CREAM = "#F3EDDD";
const DIM = "rgba(243,237,221,0.6)";
const track = (type, id = "") => axios.post(`${API}/api/guide/ecosystem/event`, { type, id }).catch(() => {});
const pill = "rounded-full px-4 py-2 text-xs transition-transform duration-200 active:scale-95";

const DoorCta = ({ d, onGo }) => (
  <button type="button" onClick={onGo} data-testid={`adelaida-door-cta-${d.id}`} className={`${pill} inline-flex items-center gap-1.5 font-semibold`} style={{ background: d.accent, color: "#0b0f0d" }}>
    {d.id === "reclaim" ? "Take one minute with me" : d.tourCta} <ArrowRight size={13} />
  </button>
);

const Tour = ({ step, setStep, onGo }) => {
  const d = DOORS[step];
  return (
    <div data-testid="adelaida-tour">
      {d ? (
        <>
          <p className="text-[0.55rem] uppercase tracking-[0.3em]" style={{ color: d.accent, fontFamily: SANS }} data-testid="adelaida-tour-step">Door {step + 1} of 7 · {d.name}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2"><DoorCta d={d} onGo={() => onGo(d)} /></div>
        </>
      ) : <p className="text-[0.55rem] uppercase tracking-[0.3em]" style={{ color: GOLD, fontFamily: SANS }}>All seven doors</p>}
      <div className="mt-4 flex items-center justify-between">
        <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0} className="text-xs inline-flex items-center gap-1 disabled:opacity-30" style={{ color: DIM }} data-testid="adelaida-tour-back"><ArrowLeft size={13} /> Back</button>
        <span className="flex gap-1">{DOORS.map((x, i) => <span key={x.id} className="w-1.5 h-1.5 rounded-full transition-colors duration-300" style={{ background: i === step ? x.accent : "rgba(243,237,221,0.2)" }} />)}</span>
        {step < 7 && <button type="button" onClick={() => setStep(step + 1)} className="text-xs inline-flex items-center gap-1" style={{ color: GOLD }} data-testid="adelaida-tour-next">{step === 6 ? "Finish" : "Next door"} <ArrowRight size={13} /></button>}
      </div>
    </div>
  );
};

const Listen = ({ needs, onPick, onType }) => {
  const [t, setT] = useState("");
  return (
    <div data-testid="adelaida-listen">
      <div className="flex flex-wrap gap-2">
        {needs.map((f) => (
          <button key={f.id} type="button" onClick={() => onPick(f)} data-testid={`adelaida-need-${f.id}`} className="rounded-full px-3 py-1.5 text-xs text-left transition-colors duration-300 hover:bg-white/10"
            style={{ border: "1px solid rgba(215,187,122,0.3)", color: CREAM, fontFamily: SANS }}>{f.q}</button>
        ))}
      </div>
      <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (t.trim().length > 1) { onType(t.trim()); setT(""); } }}>
        <input value={t} onChange={(e) => setT(e.target.value)} maxLength={200} placeholder="Or in your own words…" data-testid="adelaida-listen-input"
          className="flex-1 min-w-0 rounded-full bg-white/5 px-4 py-2 text-sm outline-none focus:ring-1" style={{ color: CREAM, border: "1px solid rgba(215,187,122,0.2)", fontFamily: SANS }} />
        <button type="submit" aria-label="Tell Adelaida" data-testid="adelaida-listen-send" className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: GOLD, color: "#0b0f0d" }}><ArrowRight size={15} /></button>
      </form>
    </div>
  );
};

export const AdelaidaOrb = ({ friend = null }) => {
  const nav = useNavigate();
  const audio = useRef(null);
  const autoOpen = useRef(null);
  const lastId = useRef(null);
  const [who, setWho] = useState(() => (localStorage.getItem("eco_guide") === "richard" ? "richard" : "adelaida"));
  const resetRef = useRef(null);
  const [g, setG] = useState(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("welcome"); // welcome | tour | listen | answer | reset
  const modeRef = useRef("welcome");
  modeRef.current = mode;
  const welcomeId = friend ? `friend:${friend.slug}` : "welcome";
  const [welcome, setWelcome] = useState("");
  const [text, setText] = useState("");
  const [answer, setAnswer] = useState(null);
  const [step, setStep] = useState(0);
  const [env, setEnv] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [needTap, setNeedTap] = useState(false);
  const [muted, setMuted] = useState(false);
  const [script, setScript] = useState(null);
  const [line, setLine] = useState("");
  const faqs = g?.faqs || [];
  const find = (id) => faqs.find((f) => f.id === id);

  useEffect(() => {
    axios.get(`${API}/api/guide/ecosystem`).then((r) => { setG(r.data); setText((t) => t || r.data.welcome); setWelcome((w) => w || r.data.welcome); }).catch(() => {});
    if (friend) axios.post(`${API}/api/guide/ecosystem/say`, { who: "adelaida", id: welcomeId }).then((r) => { setWelcome(r.data.text); setText((t) => (modeRef.current === "welcome" ? r.data.text : t)); }).catch(() => {});
    track("open");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!g || sessionStorage.getItem("eco_adelaida_closed")) return undefined;
    autoOpen.current = setTimeout(() => { setOpen(true); speak(welcomeId); }, 2600);
    return () => clearTimeout(autoOpen.current);
  }, [!!g]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = (url, e, scr = null) => {
    const a = audio.current;
    setEnv(e || null); setScript(scr); setLine("");
    a.src = `${API}${url}`; a.muted = muted;
    a.play().then(() => setNeedTap(false)).catch((err) => { if (err?.name === "NotAllowedError") setNeedTap(true); });
  };
  const speak = async (id, w = who) => {
    lastId.current = id;
    try { const r = await axios.post(`${API}/api/guide/ecosystem/say`, { who: w, id }); play(r.data.audio_url, r.data.env); } catch { /* text still shows */ }
  };
  const stop = () => { audio.current?.pause(); setSpeaking(false); };
  const show = (id, m) => { const f = find(id) || {}; setText(id === "welcome" ? g.welcome : f.a || g.fallback); setMode(m); speak(id); };
  const tourTo = (i) => { setStep(i); if (i < 7) show(`tour_${DOORS[i].id}`, "tour"); else show("tour_end", "tour"); track("question", i < 7 ? `tour_${DOORS[i].id}` : "tour_end"); };
  const pickNeed = (f) => { setAnswer(f); show(f.id, "answer"); track("question", f.id); };
  const typed = (t) => {
    const f = match(t, faqs.filter((x) => x.id.startsWith("need_")));
    track(f ? "typed" : "miss", f ? f.id : "");
    if (f) return pickNeed(f);
    axios.post(`${API}/api/guide/ecosystem/miss`, { text: t }).catch(() => {});
    setAnswer(null); setText(g.fallback); setMode("answer"); speak("fallback");
  };
  const startReset = async () => {
    clearTimeout(autoOpen.current);
    setOpen(true); setMode("reset"); setText("Let's take one minute. Just breathe with me."); track("reset", "breathe");
    try { const r = await axios.get(`${API}/api/guide/ecosystem/reset/breathe?who=${who}`); lastId.current = null; play(r.data.audio_url, r.data.env, r.data.script || []); } catch { setText("I can't play that just now. Take one slow breath anyway — in through the nose, and gently out."); }
  };
  const goDoor = (d) => { track("intro", d.id); if (d.id === "reclaim") return startReset(); stop(); goTo(d.href, nav); };
  const close = () => { stop(); setOpen(false); sessionStorage.setItem("eco_adelaida_closed", "1"); };
  const onTime = () => { if (!script) return; const t = audio.current.currentTime; const cur = [...script].reverse().find((s) => s.start <= t); setLine(cur ? cur.text : ""); };

  resetRef.current = startReset;
  useEffect(() => {
    const h = () => resetRef.current();
    window.addEventListener("adelaida:reset", h);
    return () => window.removeEventListener("adelaida:reset", h);
  }, []);

  const WHO_FALLBACK = { adelaida: { name: "Adelaida", image: "/coaches/adelaida_calm.webp" }, richard: { name: "Richard", image: "/coaches/richard_calm.webp" } };
  const person = g?.identities?.[who] || WHO_FALLBACK[who];
  const nm = (t) => (who === "adelaida" ? t : (t || "").replace(/Adelaida/g, person.name));
  const switchTo = (w) => {
    if (w === who) return;
    setWho(w); localStorage.setItem("eco_guide", w); track("intro", `guide_${w}`);
    if (lastId.current) speak(lastId.current, w); else stop();
  };
  return (
    <>
      <audio ref={audio} onPlay={() => setSpeaking(true)} onPause={() => setSpeaking(false)} onEnded={() => { setSpeaking(false); setScript(null); setLine(""); }} onTimeUpdate={onTime} />
      <AnimatePresence>
        {open && person && (
          <motion.div key="panel" initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[70] left-3 right-3 sm:left-auto sm:right-6 sm:w-[24rem] rounded-[1.75rem] p-5 max-h-[78vh] overflow-y-auto"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px + var(--soulumo-tabbar, 0px))", background: "rgba(9,16,13,0.9)", border: "1px solid rgba(215,187,122,0.28)", backdropFilter: "blur(20px)", boxShadow: "0 30px 90px -20px rgba(0,0,0,0.8)" }}
            data-testid="adelaida-panel">
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 rounded-full overflow-hidden shrink-0" style={{ boxShadow: `0 0 0 1px ${GOLD}55, 0 0 40px -6px rgba(215,187,122,0.5)` }}>
                {/* The portrait renders at 160px (192px from sm); scale it into the small orb without touching its face math. */}
                <div className="origin-top-left scale-[0.6] sm:scale-50 w-40 sm:w-48">
                  <TalkingPortrait key={who} who={person} audioEl={audio.current} env={env} speaking={speaking} status="idle" />
                </div>
              </div>
              <div className="min-w-0 flex-1 pt-1">
                <p className="text-xl" style={{ fontFamily: SERIF, color: CREAM }} data-testid="guide-name">{person.name}</p>
                <p className="text-[0.55rem] uppercase tracking-[0.28em]" style={{ color: GOLD, fontFamily: SANS }}>Your guide to Reclaim</p>
                <div className="mt-2 flex items-center gap-3">
                  <span className="inline-flex rounded-full p-0.5" style={{ border: "1px solid rgba(215,187,122,0.25)" }} data-testid="guide-switch">
                    {["adelaida", "richard"].map((w) => (
                      <button key={w} type="button" onClick={() => switchTo(w)} data-testid={`guide-switch-${w}`} aria-pressed={who === w}
                        className="rounded-full px-2.5 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] transition-colors duration-300"
                        style={{ background: who === w ? GOLD : "transparent", color: who === w ? "#0b0f0d" : DIM, fontFamily: SANS }}>{WHO_FALLBACK[w].name}</button>
                    ))}
                  </span>
                  <button type="button" onClick={() => { const m = !muted; setMuted(m); if (audio.current) audio.current.muted = m; }} aria-label={muted ? "Unmute" : "Mute"} data-testid="adelaida-mute" style={{ color: DIM }}>{muted ? <VolumeX size={15} /> : <Volume2 size={15} />}</button>
                </div>
              </div>
              <button type="button" onClick={close} aria-label="Close guide" data-testid="adelaida-close" className="p-1 opacity-60 hover:opacity-100 transition-opacity" style={{ color: CREAM }}><X size={18} /></button>
            </div>
            {needTap && (
              <button type="button" onClick={() => audio.current.play().then(() => setNeedTap(false)).catch(() => {})} data-testid="adelaida-tap-hear"
                className={`${pill} mt-4 inline-flex items-center gap-2`} style={{ border: `1px solid ${GOLD}`, color: GOLD }}><Volume2 size={13} /> Hear {person.name}</button>
            )}
            {friend && mode === "welcome" && (
              <div className="mt-4 flex items-center gap-2.5" data-testid="friend-recommendation">
                {friend.photo_url && <img src={`${API}${friend.photo_url}`} alt={friend.name} className="w-9 h-9 rounded-full object-cover" style={{ boxShadow: `0 0 0 1px ${GOLD}88` }} data-testid="friend-photo" />}
                <span className="text-[0.6rem] uppercase tracking-[0.22em]" style={{ color: GOLD, fontFamily: SANS }}>Recommended by {friend.name.split(" ")[0]}</span>
              </div>
            )}
            <p className="mt-4 text-lg leading-snug" style={{ fontFamily: SERIF, color: CREAM }} data-testid="adelaida-text">{nm(script ? (line || text) : text)}</p>
            <div className="mt-4">
              {mode === "welcome" && (
                <>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => tourTo(0)} data-testid="adelaida-show-around" className={`${pill} font-semibold`} style={{ background: GOLD, color: "#0b0f0d" }}>Show me around</button>
                    <button type="button" onClick={close} data-testid="adelaida-explore-myself" className={pill} style={{ border: "1px solid rgba(243,237,221,0.25)", color: CREAM }}>I'll explore myself</button>
                  </div>
                  <button type="button" onClick={() => { setMode("listen"); stop(); setText("What brought you here today? Tap whatever feels closest."); }} data-testid="adelaida-tell-me" className="mt-3 text-xs underline underline-offset-4" style={{ color: DIM }}>Or tell me what brought you here</button>
                </>
              )}
              {mode === "tour" && <Tour step={step} setStep={tourTo} onGo={goDoor} />}
              {mode === "listen" && <Listen needs={faqs.filter((f) => f.id.startsWith("need_"))} onPick={pickNeed} onType={typed} />}
              {mode === "answer" && (
                <div className="flex flex-wrap gap-2" data-testid="adelaida-answer">
                  {answer?.door && <DoorCta d={door(answer.door)} onGo={() => goDoor(door(answer.door))} />}
                  {answer?.reset && answer.door !== "reclaim" && <button type="button" onClick={startReset} className={pill} style={{ border: `1px solid ${GOLD}`, color: GOLD }} data-testid="adelaida-answer-reset">Try a free minute</button>}
                  <button type="button" onClick={() => { setMode("listen"); stop(); setText("Of course. What else is on your mind?"); }} className={pill} style={{ color: CREAM, border: "1px solid rgba(243,237,221,0.22)" }} data-testid="adelaida-something-else">Something else</button>
                </div>
              )}
              {mode === "tour" && step >= 7 && (
                <button type="button" onClick={() => { setMode("listen"); stop(); setText("What brought you here today? Tap whatever feels closest."); }} className="mt-3 text-xs underline underline-offset-4" style={{ color: DIM }} data-testid="adelaida-tour-tell-me">Tell me what brought you here</button>
              )}
              {mode === "reset" && <button type="button" onClick={() => { stop(); setScript(null); setMode("welcome"); setText(welcome); }} className={pill} style={{ color: CREAM, border: "1px solid rgba(243,237,221,0.22)" }} data-testid="adelaida-reset-done">Back</button>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!open && person && (
        <motion.button type="button" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} onClick={() => { setOpen(true); if (mode === "welcome") speak(welcomeId); }}
          aria-label={`Talk to ${person.name}`} data-testid="adelaida-guide-orb"
          className="fixed z-[70] right-4 sm:right-6 flex items-center gap-2 rounded-full pl-1 pr-4 py-1 transition-transform duration-300 hover:-translate-y-0.5"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 18px + var(--soulumo-tabbar, 0px))", background: "rgba(9,16,13,0.85)", border: `1px solid ${GOLD}`, backdropFilter: "blur(14px)", boxShadow: "0 0 0 6px rgba(215,187,122,0.06), 0 16px 40px -12px rgba(0,0,0,0.7)" }}>
          <span className="relative">
            <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "rgba(215,187,122,0.25)", animationDuration: "3s" }} />
            <img src={person.image} alt="" className="relative w-11 h-11 rounded-full object-cover object-top" />
          </span>
          <span className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD, fontFamily: SANS }}>{person.name}</span>
        </motion.button>
      )}
    </>
  );
};
